import React, { useState } from "react";
import "./AccountingBookeeping.css";
import ServiceContactModal from "../ServiceContactModal/ServiceContactModal"; // adjust path if needed

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileInvoiceDollar,
  faMoneyBillTrendUp,
  faUsersGear,
  faChartLine,
  faShieldAlt,
  faLightbulb,
  faCalendarCheck,
  faBuildingColumns,
} from "@fortawesome/free-solid-svg-icons";


const services = [
  {
    title: "Monthly Bookkeeping",
    tag: "Core",
    icon: faFileInvoiceDollar,
    desc: "Accurate categorisation of income and expenses, reconciled accounts, and clean books delivered every month.",
  },
  {
    title: "Accounts Payable & Receivable",
    tag: "Cash Flow",
    icon: faMoneyBillTrendUp,
    desc: "Structured handling of vendor bills and client invoices so you always know what’s due in or out.",
  },
  {
    title: "Payroll Processing & Compliance",
    tag: "People",
    icon: faUsersGear,
    desc: "End-to-end payroll runs, tax withholdings and filings so your US team is paid correctly and on time.",
  },
  {
    title: "Financial Statements & Reporting",
    tag: "Insights",
    icon: faChartLine,
    desc: "GAAP-aligned P&L, balance sheet and cash-flow reports that drive confident decision-making.",
  },
  {
    title: "Tax-Ready Books & Year-End Support",
    tag: "Compliance",
    icon: faShieldAlt,              // <-- changed here
    desc: "Year-end adjustments, 1099 support and CPA collaboration so your books stay audit-friendly.",
  },
  {
    title: "Virtual CFO & Advisory",
    tag: "Strategy",
    icon: faLightbulb,
    desc: "Budgeting, forecasting and KPI dashboards that turn your numbers into a growth roadmap.",
  },
];


const whyChooseUs = [
  "US-focused team that understands federal and state compliance requirements.",
  "Cloud-based stack (QuickBooks Online, Xero, NetSuite) for real-time access from anywhere.",
  "Standardised month-end close process so every period is clean, consistent and on time.",
  "Proactive communication with clear SLAs, not silent vendors you have to chase.",
  "We plug into your existing CPA/tax advisor instead of replacing relationships that already work.",
];

const processSteps = [
  {
    title: "1. Discover & Clean Up",
    desc: "We review your current books, tools and workflows, then fix historical gaps so the baseline is trusted.",
  },
  {
    title: "2. Standardise & Automate",
    desc: "We document recurring tasks, implement best practices and automate as much as possible with rules and workflows.",
  },
  {
    title: "3. Maintain & Advise",
    desc: "Your books stay updated each month, with clear reports and a finance partner who joins your strategic decisions.",
  },
];

const testimonials = [
  {
    name: "SaaS Founder, California",
    role: "Seed-stage B2B SaaS",
    quote:
      "They cleaned up two years of messy books and now I get investor-ready reports every month without chasing anyone.",
  },
  {
    name: "E-commerce Brand, Texas",
    role: "7-figure Shopify store",
    quote:
      "Our payables and receivables are finally under control. Cash flow calls are now based on actual data, not guesses.",
  },
  {
    name: "Professional Services Firm, New York",
    role: "Consulting agency",
    quote:
      "Month-end close went from chaos to a predictable 5-day cycle. Their virtual CFO inputs are part of every strategy meeting.",
  },
];

const industries = [
  "SaaS & Tech",
  "E-commerce & DTC",
  "Agencies & Creatives",
  "Healthcare Practices",
  "Consulting & Professional Services",
  "Real Estate & Property",
];

const faqs = [
  {
    q: "Do you only work with US-based businesses?",
    a: "We specialise in US entities (LLCs, C-Corps, S-Corps) and understand US compliance, but we also support founders and shareholders based internationally who operate US companies.",
  },
  {
    q: "Which accounting tools do you support?",
    a: "Our primary stack includes QuickBooks Online, Xero and NetSuite. We also work with Stripe, Shopify, PayPal and other connectors for clean reconciliations.",
  },
  {
    q: "Can you work with my existing CPA or tax firm?",
    a: "Yes. We focus on bookkeeping and internal reporting, and collaborate closely with your CPA so tax filing and year-end become a simple handoff.",
  },
  {
    q: "What does onboarding look like?",
    a: "Typically 2–4 weeks. We review access, map your chart of accounts, clean up historical data (if needed) and agree on monthly deliverables, timelines and communication channels.",
  },
];

const metrics = [
  { label: "Years focused on US books", value: "10+" },
  { label: "States we’ve supported", value: "20+" },
  { label: "Books closed per year", value: "500+" },
  { label: "Client satisfaction score", value: "4.9/5" },
];

const AccountingBookeeping = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleTalkToUs = () => {
    setIsModalOpen(true);
  };

  return (
    <>
      <section className="ab-section">
        <div className="ab-inner">
          {/* Hero layout: copy + visual */}
          <div className="ab-hero">
            {/* Hero / top intro */}
            <header className="ab-header">
              <p className="ab-eyebrow">ACCOUNTING &amp; BOOKKEEPING</p>
              <h1 className="ab-title">
                Smart Accounting &amp; Bookkeeping Services in USA
              </h1>
              <p className="ab-sub">
                From solo founders to growing US enterprises, we become your
                always-on finance partner—keeping your books accurate,
                compliant and ready for confident decisions every month.
              </p>

              <div className="ab-pill-row">
                <span className="ab-pill">US GAAP-aligned</span>
                <span className="ab-pill">
                  Cloud-first &amp; remote-friendly
                </span>
                <span className="ab-pill">CPA &amp; investor ready</span>
              </div>

              <div className="ab-cta-row">
                <button
                  type="button"
                  className="ab-cta-primary"
                  onClick={handleTalkToUs}
                >
                  Talk to us about your books
                </button>
                <p className="ab-cta-note">
                  No obligation. Share where you are today and we’ll map the
                  next 90 days in plain English.
                </p>
              </div>
            </header>

            {/* Hero visual / image-style card */}
            <div className="ab-hero-visual">
              <div className="ab-hero-card">
                <div className="ab-hero-card-top">
                  <div className="ab-hero-icon">
                    <FontAwesomeIcon icon={faBuildingColumns} />
                  </div>
                  <div>
                    <p className="ab-hero-label">Live financial snapshot</p>
                    <p className="ab-hero-title">This Month at a Glance</p>
                  </div>
                </div>

                <div className="ab-hero-stats">
                  <div className="ab-hero-stat">
                    <span className="ab-hero-stat-label">Revenue</span>
                    <span className="ab-hero-stat-value">$248,900</span>
                  </div>
                  <div className="ab-hero-stat">
                    <span className="ab-hero-stat-label">Net Margin</span>
                    <span className="ab-hero-stat-value">18.4%</span>
                  </div>
                  <div className="ab-hero-stat">
                    <span className="ab-hero-stat-label">Invoices Paid</span>
                    <span className="ab-hero-stat-value">96%</span>
                  </div>
                </div>

                <div className="ab-hero-footer">
                  <div className="ab-hero-footer-left">
                    <FontAwesomeIcon
                      icon={faCalendarCheck}
                      className="ab-hero-footer-icon"
                    />
                    <span>Month-end close scheduled in 3 days</span>
                  </div>
                  <button
                    type="button"
                    className="ab-hero-footer-btn"
                    onClick={handleTalkToUs}
                  >
                    Review my books
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Metrics strip */}
          <div className="ab-metrics">
            {metrics.map((m, idx) => (
              <div key={idx} className="ab-metric">
                <span className="ab-metric-value">{m.value}</span>
                <span className="ab-metric-label">{m.label}</span>
              </div>
            ))}
          </div>

          {/* Services grid */}
          <section className="ab-block">
            <div className="ab-block-header">
              <h2 className="ab-section-title">
                Core Accounting &amp; Bookkeeping Services
              </h2>
              <p className="ab-section-sub">
                Choose the combination that matches where your business is
                today. We can support you with a single service or a complete
                finance stack.
              </p>
            </div>
            <div className="ab-grid">
              {services.map((service, idx) => (
                <article key={idx} className="ab-card">
                  <div className="ab-card-icon-wrap">
                    <div className="ab-card-icon">
                      <FontAwesomeIcon icon={service.icon} />
                    </div>
                    <div className="ab-card-tag">{service.tag}</div>
                  </div>
                  <h3 className="ab-card-title">{service.title}</h3>
                  <p className="ab-card-desc">{service.desc}</p>
                </article>
              ))}
            </div>
          </section>

          {/* Why choose us + Process + Testimonials */}
          <section className="ab-info-row">
            <div className="ab-why">
              <h2 className="ab-section-title">Why US businesses choose us</h2>
              <p className="ab-section-sub">
                We go beyond basic data entry to give you a consistent,
                well-documented finance operation that scales as you grow.
              </p>
              <ul className="ab-why-list">
                {whyChooseUs.map((point, idx) => (
                  <li key={idx}>{point}</li>
                ))}
              </ul>

              <div className="ab-process">
                <h3 className="ab-process-title">How we work with you</h3>
                <div className="ab-process-grid">
                  {processSteps.map((step, idx) => (
                    <div key={idx} className="ab-process-item">
                      <span className="ab-process-step">
                        {String(idx + 1).padStart(2, "0")}
                      </span>
                      <div>
                        <h4>{step.title}</h4>
                        <p>{step.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="ab-testimonials">
              <h2 className="ab-section-title">What our clients say</h2>
              <div className="ab-testimonial-grid">
                {testimonials.map((t, idx) => (
                  <figure key={idx} className="ab-testimonial-card">
                    <p className="ab-testimonial-quote">“{t.quote}”</p>
                    <figcaption className="ab-testimonial-meta">
                      <span className="ab-testimonial-name">{t.name}</span>
                      <span className="ab-testimonial-role">{t.role}</span>
                    </figcaption>
                  </figure>
                ))}
              </div>
            </div>
          </section>

          {/* Industries */}
          <section className="ab-block">
            <div className="ab-block-header">
              <h2 className="ab-section-title">Industries we support</h2>
              <p className="ab-section-sub">
                We adapt our playbooks to the realities of your vertical while
                keeping your books consistent and comparable month after month.
              </p>
            </div>
            <div className="ab-industry-row">
              {industries.map((item, idx) => (
                <span key={idx} className="ab-industry-chip">
                  {item}
                </span>
              ))}
            </div>
          </section>

          {/* FAQ */}
          <section className="ab-block">
            <div className="ab-block-header">
              <h2 className="ab-section-title">Frequently asked questions</h2>
              <p className="ab-section-sub">
                Still not sure if we’re the right fit? These are the questions
                founders ask us most often before we start working together.
              </p>
            </div>
            <div className="ab-faq-list">
              {faqs.map((item, idx) => (
                <details key={idx} className="ab-faq-item">
                  <summary>{item.q}</summary>
                  <p>{item.a}</p>
                </details>
              ))}
            </div>
          </section>

          {/* Bottom contact strip */}
          <section className="ab-bottom-cta">
            <div className="ab-bottom-cta-inner">
              <div>
                <h2>Ready for clean, tax-ready books every month?</h2>
                <p>
                  Share a few details about your US business and we’ll come back
                  with a tailored bookkeeping plan, timelines and transparent
                  pricing—no hard sell.
                </p>
              </div>
              <button
                type="button"
                className="ab-cta-secondary"
                onClick={handleTalkToUs}
              >
                Schedule a free consultation
              </button>
            </div>
          </section>
        </div>
      </section>

      {/* Contact Modal */}
      <ServiceContactModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        defaultService="Accounting & Bookkeeping Services in USA"
      />
    </>
  );
};

export default AccountingBookeeping;
